import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LOGO_SRC } from '../BrandLogo';
import { BRAND, GED_SUBJECTS } from '../../data/siteContent';
import { useStudyPlans } from '../../hooks/useStudyPlans';
import { useAuth } from '../../context/AuthContext';
import { getPortalHomePath } from '../../utils/portalPaths';
import { trackSiteVisit } from '../../utils/visitorTracking';
import SeoHead from '../SeoHead';
import studentSmile from '../../assets/images/student-smile.png';
import '../../styles/marketing.css';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  {
    to: '/program',
    label: 'GED Program',
    children: [
      { to: '/program', label: 'Program Overview' },
      ...GED_SUBJECTS.map((s) => ({ to: `/program/${s.slug}`, label: s.title })),
    ],
  },
  { to: '/pricing', label: 'Pricing' },
  {
    to: '/blog',
    label: 'Resources',
    children: [
      { to: '/how-it-works', label: 'How It Works' },
      { to: '/success-stories', label: 'Success Stories' },
      { to: '/blog', label: 'Blog' },
      { to: '/faq', label: 'FAQ' },
    ],
  },
  { to: '/contact', label: 'Contact' },
];

function digitsOnlyPhone(phone) {
  return String(phone || '').replace(/[^\d]/g, '');
}

function whatsappHref(phone, message = '') {
  const digits = digitsOnlyPhone(phone);
  if (!digits) return '#';
  const base = `https://wa.me/${digits}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

export default function MarketingLayout({ children, seoTitle, seoDescription }) {
  const [open, setOpen] = useState(false);
  const [navStuck, setNavStuck] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { adminContactPhone } = useStudyPlans();
  const { currentUser, isAdmin, hasSubscription } = useAuth();

  const phoneDisplay = adminContactPhone || '+44 7345 060051';
  const waHref = whatsappHref(
    phoneDisplay,
    `Hello ${BRAND.name}, I would like to know more about GED preparation.`
  );

  const enrollCta = (() => {
    if (isAdmin) {
      return { to: '/admin', label: 'Admin Console' };
    }
    if (currentUser && hasSubscription) {
      return { to: getPortalHomePath({ isAdmin }), label: 'Dashboard' };
    }
    if (currentUser) {
      return { to: '/billing', label: 'Complete Enrollment' };
    }
    return { to: '/signup', label: 'Enroll Now' };
  })();

  useEffect(() => {
    trackSiteVisit(location.pathname).catch(console.warn);
  }, [location.pathname]);

  useEffect(() => {
    setOpen(false);
    setNavStuck(false);
    window.scrollTo(0, 0);
  }, [location.pathname, location.key]);

  useEffect(() => {
    if (!isHome) {
      setNavStuck(false);
      return undefined;
    }

    const STICK_AT = 24;
    const onScroll = () => {
      setNavStuck(window.scrollY > STICK_AT);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  return (
    <div className={`mk-page${isHome ? ' mk-page--shaped-hero' : ''}`}>
      <SeoHead title={seoTitle} description={seoDescription} />
      <header
        ref={navRef}
        className={`mk-nav${isHome ? ' mk-nav--in-hero' : ''}${navStuck ? ' is-stuck' : ''}`}
      >
        <div className="mk-nav-inner">
          <Link to="/" className="mk-brand" aria-label={`${BRAND.name} home`}>
            <img src={LOGO_SRC} alt={BRAND.name} className="mk-brand-logo" width={52} height={52} />
            <span className="mk-brand-text">
              <span className="mk-brand-name">{BRAND.name}</span>
              <span className="mk-brand-tag">{BRAND.tagline}</span>
            </span>
          </Link>

          <nav className="mk-nav-links" aria-label="Primary">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.to} className="mk-nav-dropdown">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `mk-nav-link${isActive ? ' is-active' : ''}`}
                  >
                    {item.label}
                    <span className="material-symbols-outlined mk-nav-caret">expand_more</span>
                  </NavLink>
                  <div className="mk-nav-dropdown-panel">
                    {item.children.map((c) => (
                      <Link key={c.to} to={c.to}>
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `mk-nav-link${isActive ? ' is-active' : ''}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="mk-nav-actions">
            {!currentUser ? (
              <Link to="/login" className="mk-btn mk-btn-ghost">
                Sign In
              </Link>
            ) : null}
            <Link to={enrollCta.to} className="mk-btn mk-btn-primary mk-btn-pill">
              {enrollCta.label}
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                arrow_forward
              </span>
            </Link>
          </div>

          <button
            type="button"
            className="mk-nav-toggle"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="material-symbols-outlined">{open ? 'close' : 'menu'}</span>
          </button>
        </div>
      </header>

      <div className={`mk-mobile-sheet${open ? ' is-open' : ''}`}>
        {NAV.map((item) => (
          <div key={item.to}>
            <Link to={item.to} onClick={() => setOpen(false)}>
              {item.label}
            </Link>
            {item.children && (
              <div className="mk-mobile-sub">
                {item.children.map((c) => (
                  <Link key={c.to} to={c.to} onClick={() => setOpen(false)}>
                    {c.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
        <Link to={currentUser ? enrollCta.to : '/login'} className="mk-btn mk-btn-secondary" onClick={() => setOpen(false)}>
          {currentUser ? enrollCta.label : 'Sign In'}
        </Link>
        {!currentUser ? (
          <Link to={enrollCta.to} className="mk-btn mk-btn-primary" onClick={() => setOpen(false)}>
            {enrollCta.label}
          </Link>
        ) : null}
      </div>

      <main>{children}</main>

      <section className="mk-support-band">
        <div className="mk-container mk-support-inner">
          <div className="mk-support-person" aria-hidden="true">
            <img src={studentSmile} alt="" />
          </div>
          <div>
            <h3>Ready to start?</h3>
            <p>Enroll, pick a plan, and study with the AI tutor.</p>
          </div>
          <div className="mk-support-actions">
            <Link to={enrollCta.to} className="mk-btn mk-btn-gold">
              {enrollCta.label}
            </Link>
          </div>
        </div>
      </section>

      <footer className="mk-footer">
        <div className="mk-container">
          <div className="mk-footer-grid">
            <div className="mk-footer-brand">
              <img src={LOGO_SRC} alt="" width={64} height={64} />
              <div>
                <strong className="mk-footer-title">{BRAND.name}</strong>
                <p>AI tutoring, practice exams, and a clear study plan.</p>
                <div className="mk-footer-hours">
                  <span className="material-symbols-outlined">schedule</span>
                  Support: Mon – Sat · 8:00 – 18:00
                </div>
              </div>
            </div>
            <div>
              <h4>Explore</h4>
              <Link to="/">Home</Link>
              <Link to="/about">About Us</Link>
              <Link to="/program">GED Program</Link>
              <Link to="/how-it-works">How It Works</Link>
              <Link to="/pricing">Pricing</Link>
              <Link to="/checkout">Checkout</Link>
            </div>
            <div>
              <h4>Resources</h4>
              <Link to="/success-stories">Success Stories</Link>
              <Link to="/blog">Blog</Link>
              <Link to="/faq">FAQ</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div>
              <h4>Legal</h4>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/refund-cancellation-policy">Refund &amp; Cancellation</Link>
              <Link to="/service-delivery-policy">Service Delivery</Link>
              <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
              <Link to="/contact">Contact Us</Link>
            </div>
          </div>
          <div className="mk-legal">
            <span>
              © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
            </span>
            <span>Professional GED AI learning — Success Bridge Academy.</span>
          </div>
        </div>
      </footer>

      <a
        href={waHref}
        className="mk-whatsapp-fab"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Message ${BRAND.name} on WhatsApp at ${phoneDisplay}`}
        title={`WhatsApp ${phoneDisplay}`}
      >
        <svg viewBox="0 0 32 32" width="28" height="28" aria-hidden="true">
          <path
            fill="currentColor"
            d="M16.01 3C9.39 3 4 8.39 4 15.02c0 2.11.55 4.16 1.6 5.97L4 29l8.2-2.15A12 12 0 0 0 16.01 27C22.63 27 28 21.61 28 14.98 28 8.39 22.63 3 16.01 3zm0 21.96c-1.82 0-3.6-.49-5.16-1.41l-.37-.22-4.87 1.28 1.3-4.74-.24-.39A9.92 9.92 0 0 1 6.1 15.02c0-5.47 4.44-9.92 9.91-9.92s9.91 4.45 9.91 9.92c0 5.47-4.44 9.94-9.91 9.94zm5.44-7.43c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z"
          />
        </svg>
      </a>
    </div>
  );
}
